import { useCallback, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery } from "@tanstack/react-query";

import {
  ApprovalModel,
  ApprovalTypeEnum,
  Approver,
  CostCenter,
  CustomerEmployee,
  Level,
  ModelTypeEnum,
  Phase,
  Project,
} from "~/application/types";
import { Box } from "~/components/Box";
import { Button } from "~/components/Button";
import { Form } from "~/components/Form/Form";
import { FieldLabel, FormControl } from "~/components/FormControl";
import { Col, Row } from "~/components/Grid";
import { Select, Switch, TextInput } from "~/components/Input";
import { Text } from "~/components/Text";
import { H5 } from "~/components/Typography";
import { AsyncSelect } from "../../components/AsyncSelect";
import { ApprovalLevelListItem } from "./ApprovalLevelListItem";
import {
  ApprovalModelDialogProps,
  approvalModelSchema,
  ApprovalModelSchema,
  ApproverSchema,
  LevelSchema,
  OnSelectApproverParams,
} from "./types";
import { approvalTypeOptions, getDefaultValues, modelTypeOptions } from "./utils";
import { MultiSelectWrap } from "~/components/Input/MultiSelectWrap";
import { usePhases } from "~/core/modules/Customer/pages/CustomerPage/hooks/usePhases";
import { useProjects } from "~/core/modules/Customer/pages/CustomerPage/hooks/useProjects";
import {
  GroupEmployee,
  useCustomerEmployeesGroups,
} from "~/core/modules/Customer/pages/CustomerPage/hooks/useCustomerEmployeesGroups";
import { Flex } from "~/components/Flex";
import {
  approvalModelService,
  branchService,
  customerEmployeeService,
} from "~/application/usecases";
import { Alert, AlertIcon } from "~/components/Alert";
import { Icon } from "~/components/Icon";
import { SvgInfo, SvgClose } from "~/components/Icon/icons";
import { CustomerTab } from "~/core/modules/Customer/utils";
import { dialogService } from "~/components/DialogStack";
import { useCostCenters } from "~/core/modules/Customer/pages/CustomerPage/hooks/useCostCenters";
import { QueryKeys } from "~/constants";
import { useUser } from "~/presentation/core/contexts/UserContext";
import { useDebounce } from "use-debounce";

export function ApprovalModelDialog({
  isNew,
  approvalModels,
  isLoadingApprovalModels,
  defaultData,
  fetchApprovalModels,
  fetchApprovers,
  onCloseClick,
  onSubmit,
  customerId,
  isIssuer = false,
}: ApprovalModelDialogProps) {
  const { contexts } = useUser();
  const [markAllCostCenters, setCanMarkAllCostCenters] = useState(false);
  const [availableApprovalType, setAvailableApprovalType] = useState(defaultData?.approvalType);
  const [isSelectApproverGroup, setSelectApproverGroup] = useState(false);
  const [isRemoveApproverGroup, setRemoveApproverGroup] = useState(false);
  const [searchApprover, setSearchApprover] = useState("");
  const [approver] = useDebounce(searchApprover, 500);

  const defaultValues = getDefaultValues({ defaultData: defaultData as ApprovalModel, isNew });
  const { control, formState, handleSubmit, watch, setValue, trigger } =
    useForm<ApprovalModelSchema>({
      defaultValues,
      resolver: yupResolver(approvalModelSchema),
    });

  const {
    branchName,
    isAllBranches,
    levels,
    approvalType,
    projects,
    groups,
    groupApprovers,
    modelTypes,
  } = watch();

  const matrizUuid = contexts?.customer?.uuid || customerId;

  const selectedModelTypes = (modelTypes ?? []).filter((v): v is ModelTypeEnum =>
    Object.values(ModelTypeEnum).includes(v as ModelTypeEnum),
  );
  const isOnlyBrokenPolicyModel =
    selectedModelTypes.includes(ModelTypeEnum.BROKEN_POLICY) && selectedModelTypes.length === 1;

  const { data: availableApprovers } = useQuery(
    [
      QueryKeys.CUSTOMER_EMPLOYEES,
      customerId,
      branchName?.uuid,
      contexts,
      matrizUuid,
      approver,
      isOnlyBrokenPolicyModel,
    ],
    async () => {
      return customerEmployeeService
        .find({
          customerId,
          isActive: "Ativo",
          onlyUser: true,
          approver: true,
          name: approver,
          headquartersAccess: branchName?.uuid === matrizUuid,
          branchUuids: [branchName?.uuid ?? ""],
        })
        .then(({ data }) => data);
    },
    {
      cacheTime: 0,
      retry: 2,
    },
  );

  const { data: branches, isLoading: isLoadingBranches } = useQuery(
    [QueryKeys.CUSTOMER_BRANCHES, customerId],
    async () =>
      await branchService.find({
        customerId: customerId,
        withHeadquarters: true,
        isActive: true,
      }),
  );

  const isPhaseType = approvalType === ApprovalTypeEnum.PHASE;
  const isProjectType = approvalType === ApprovalTypeEnum.PROJECT;
  const isCostCenterType = approvalType === ApprovalTypeEnum.COST_CENTER;
  const isGroupType = approvalType === ApprovalTypeEnum.GROUP;
  const approvalTypeName = approvalTypeOptions.find(({ value }) => value === approvalType)?.label;

  const {
    data: defaultGroups,
    onSearch: fetchGroups,
    isLoading: isLoadingGroups,
  } = useCustomerEmployeesGroups({
    modelTypes: selectedModelTypes,
    customerId,
    active: true,
    enabled: isGroupType && !isOnlyBrokenPolicyModel,
    withApprovalModels: false,
    groupIds: groupApprovers?.map(({ uuid }) => uuid),
  });

  const {
    data: defaultApprovalGroups,
    onSearch: fetchApprovalGroups,
    onSearchEmployee: fetchApprovalGroupEmployees,
    groupEmployees: defaultApprovalGroupEmployees,
    isLoading: isLoadingApprovalGroups,
  } = useCustomerEmployeesGroups({
    modelTypes: selectedModelTypes,
    customerId,
    active: true,
    enabled: isGroupType && !isOnlyBrokenPolicyModel,
    groupIds: groupApprovers?.map(({ uuid }) => uuid),
  });

  const [markAllGroups, setCanMarkAllGroups] = useState(false);
  const [availableGroups, setAvailableGroups] = useState(
    defaultGroups?.map(({ uuid, description }) => ({ uuid, name: description })),
  );

  const [markAllEmployeeGroups, setCanMarkAllEmployeeGroups] = useState(false);
  const [availableEmployeeGroups, setAvailableEmployeeGroups] = useState(
    defaultApprovalGroups?.map(({ uuid, description }) => ({ uuid, name: description })),
  );

  const { data: defaultProjects, onSearch: fetchProjects } = useProjects({
    enabled: isProjectType || isPhaseType,
    isActive: true,
    customerId,
    isOnlyWithPhase: isPhaseType ? true : undefined,
    modelTypes: selectedModelTypes,
    withApprovalModels: true,
    ignoreApprovalModelId: defaultData?.uuid,
  });

  const {
    costCentersWithoutApprovalModelLink: defaultCostCenters,
    setSearchText: fetchCostCenters,
  } = useCostCenters({
    enabled: false,
    customerId,
    withApprovalModels: isCostCenterType,
    modelTypes: selectedModelTypes,
    isActive: true,
    branchIds: branchName?.uuid ? [branchName.uuid] : undefined,
    approvalModelId: defaultData?.uuid,
    headquartersOnly: branchName?.uuid === matrizUuid,
  });

  const { data: linkedApprovalModels, isLoading } = useQuery(
    [defaultData],
    async () => await approvalModelService.getAllLikedApprovallModels(defaultData?.uuid as string),
    {
      enabled: !!defaultData?.uuid,
    },
  );

  const { data: defaultPhases, onSearch: fetchPhases } = usePhases({
    enabled: isPhaseType && !!projects?.at(0)?.uuid,
    projectId: projects?.at(0)?.uuid || "",
    withApprovalModels: true,
    modelTypes: selectedModelTypes,
    ignoreApprovalModelId: defaultData?.uuid,
  });
  const [markAllProjects, setCanMarkAllProjects] = useState(false);
  const [availableProjects, setAvailableProjects] = useState(defaultProjects);

  const [markAllPhases, setCanMarkAllPhases] = useState(false);
  const [availablePhases, setAvailablePhases] = useState(defaultPhases);

  const [hasAcknowledgedWarning, setHasAcknowledgedWarning] = useState(isNew ? true : false);

  useEffect(() => {
    if (defaultPhases) {
      setAvailablePhases(defaultPhases);
    }
    if (defaultProjects) {
      setAvailableProjects(defaultProjects);
    }
    if (defaultGroups) {
      setAvailableGroups(
        defaultGroups.map((group) => ({ name: group.description, uuid: group.uuid })),
      );
    }
    if (defaultApprovalGroups) {
      setAvailableEmployeeGroups(
        defaultApprovalGroups.map((group) => ({ name: group.description, uuid: group.uuid })),
      );
    }
  }, [defaultPhases, defaultProjects, defaultGroups, defaultApprovalGroups]);

  const { append, update, remove, insert } = useFieldArray({
    control,
    name: "levels",
    keyName: "uuid",
  });

  const defaultApprovers = (
    isGroupType && !isOnlyBrokenPolicyModel
      ? (defaultApprovalGroupEmployees ?? [])
      : (availableApprovers ?? [])
  ).filter(
    (approver) =>
      !levels?.some((l) => l.approvers.some((a) => a.uuid === approver.uuid)) &&
      approver.isActive !== false,
  ) as Approver[];

  const handleApproverSelection = async ({ approver, levelIndex }: OnSelectApproverParams) => {
    const level = levels.at(levelIndex);

    const hasApproverWithSameUuidInThisLevel = level?.approvers.some(
      (a) => a.uuid === approver.uuid,
    );

    if (hasApproverWithSameUuidInThisLevel) {
      return;
    }

    const levelApprovers = (level?.approvers ?? []).filter((a) => a.uuid.length > 0);
    const newApprovers = [...levelApprovers, approver] as ApproverSchema[];

    update(levelIndex, { ...level, approvers: newApprovers });

    await trigger("levels");
  };

  const onAddLevel = (level: Level, levelIndex: number) => {
    if (levelIndex === levels.length) {
      return append(level as LevelSchema);
    }

    insert(levelIndex, level as LevelSchema);
  };

  const onRemoveLevel = (levelIndex: number) => {
    if (levels.length === 1) {
      return;
    }

    remove(levelIndex);
  };

  const onRemoveApprover = (approver: Approver, levelIndex: number) => {
    const level = levels.at(levelIndex);
    const newApprovers = (level?.approvers ?? []).filter((a) => a.uuid !== approver.uuid);

    update(levelIndex, { ...level, approvers: newApprovers });
  };

  useEffect(() => {
    if (availableApprovalType !== approvalType) {
      setCanMarkAllCostCenters(false);
      setCanMarkAllEmployeeGroups(false);
      setCanMarkAllGroups(false);
      setCanMarkAllPhases(false);
      setCanMarkAllProjects(false);
      setValue("groupApprovers", undefined);
      setValue("groups", []);
      setValue("costCenters", []);
      setValue("phases", []);
      setValue("projects", []);
      setAvailableApprovalType(approvalType);
    }
  }, [approvalType, availableApprovalType]);

  useEffect(() => {
    if (
      isGroupType &&
      !isOnlyBrokenPolicyModel &&
      isSelectApproverGroup &&
      !!groupApprovers?.length &&
      !!defaultApprovers.length
    ) {
      const level = levels.at(0);
      update(0, { ...level, approvers: defaultApprovers as ApproverSchema[] });
      setSelectApproverGroup(false);
    }
  }, [
    defaultApprovers,
    isGroupType,
    isOnlyBrokenPolicyModel,
    isSelectApproverGroup,
    groupApprovers?.length,
  ]);

  const handleOnSubmit = async (data: ApprovalModelSchema) => {
    const formattedLevels = levels.map((level, levelIndex) => {
      return {
        ...level,
        approvers: level.approvers.map((approver) => ({
          ...approver,
          level: levelIndex + 1,
        })),
      };
    }) as Level[];

    onSubmit({
      ...data,
      levels: formattedLevels,
      markAllCostCenters,
      markAllEmployeeGroups,
      markAllGroups,
      markAllProjects,
      markAllPhases,
    });
  };

  useEffect(() => {
    if (isRemoveApproverGroup) {
      (levels as Level[]).forEach((level, levelIndex) => {
        level.approvers.forEach((approver) => {
          if (
            !groupApprovers?.find((groupApprover) => groupApprover.uuid === approver.group?.uuid)
          ) {
            onRemoveApprover(approver, levelIndex);
          }
        });
      });
      setRemoveApproverGroup(false);
    }
  }, [levels, groupApprovers, isRemoveApproverGroup]);

  const fetchApproversWithOptions = useCallback(
    (name: string, options?: { headquartersAccess?: boolean }) => {
      const isMatrixSelected = branchName?.uuid === matrizUuid || isAllBranches;

      if (isMatrixSelected) {
        return fetchApprovers(name, { ...options, headquartersAccess: true });
      }

      return fetchApprovers(name, options);
    },
    [fetchApprovers, branches?.data, branchName?.uuid, isAllBranches, matrizUuid, contexts],
  );

  return (
    <Flex direction="column" css={{ flex: 1, height: "100%", overflow: "auto" }}>
      <Flex
        align="center"
        justify="between"
        css={{
          padding: "$5",
          borderBottom: "1px solid $neutrals-light",
          backgroundColor: "$primary-dark",
          zIndex: 9999,
          "@mxlg": {
            display: "none",
          },
        }}
      >
        <Flex align="center" gap="4">
          <Text fw="500" variant="white">
            {isNew ? "Novo modelo de aprovação" : "Editar modelo de aprovação"}
          </Text>
        </Flex>

        <Icon
          as={SvgClose}
          size="md"
          variant="white"
          onClick={() => onCloseClick?.()}
          css={{ cursor: "pointer" }}
        />
      </Flex>

      <Form
        onSubmit={handleSubmit((data) =>
          handleOnSubmit({
            ...data,
            branchName: branchName?.uuid === matrizUuid ? null : branchName,
          }),
        )}
        css={{
          flex: 1,
          flexDirection: "column",
          height: "auto",
          overflow: "auto",
        }}
      >
        <Flex direction="column" gap="4" css={{ flex: 1, p: "$6" }}>
          {isIssuer && (
            <Alert variant="info" css={{ mb: "$4" }}>
              <Text>
                <strong>Visualização em modo somente leitura:</strong> Como emissor, você pode
                visualizar todos os aprovadores e detalhes do modelo para dar suporte ao cliente,
                mas não pode fazer alterações.
              </Text>
            </Alert>
          )}

          <Box css={{ mb: "$4" }}>
            <H5>Informações básicas</H5>
          </Box>

          {!!linkedApprovalModels?.length && !isLoading && (
            <Alert
              variant="info"
              css={{
                p: "$4",
                mb: "$4",
              }}
            >
              <Flex gap="2" direction={"column"}>
                <Flex gap="2" align="center">
                  <Icon as={SvgInfo} />
                  <Text>
                    Este modelo de aprovação está vinculado a outros modelos de aprovação.
                  </Text>
                </Flex>
                <Flex gap="2" wrap={"wrap"}>
                  {linkedApprovalModels.map((model) => (
                    <Text
                      css={{
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      key={model.uuid}
                      onClick={() => {
                        dialogService.popAll();
                        const currentPathname = window.location.pathname;
                        window.open(
                          `${currentPathname}?modelo-de-aprovacao=${model?.uuid}#${CustomerTab.APPROVAL_MODELS}`,
                          "_blank",
                        );
                      }}
                    >
                      #{model.name}
                    </Text>
                  ))}
                </Flex>
              </Flex>
            </Alert>
          )}

          <Row gap="4">
            <Col sz="12">
              <FormControl name="modelTypes" control={control} required>
                <FieldLabel>Tipo de modelo</FieldLabel>
                {isNew ? (
                  <MultiSelectWrap
                    options={modelTypeOptions}
                    dataCy="approval-model-model-type"
                    value={modelTypeOptions.filter((data) =>
                      (modelTypes || []).includes(data.value),
                    )}
                    onChange={(items) =>
                      setValue(
                        "modelTypes",
                        (items as { value: ModelTypeEnum }[]).map(({ value }) => value),
                      )
                    }
                    placeholder="Selecione o tipo de modelo"
                    size="sm"
                    disabled={isIssuer}
                  />
                ) : (
                  <Select
                    options={modelTypeOptions}
                    value={modelTypeOptions.find((data) => data.value === modelTypes?.at(0))}
                    onChange={(data) => setValue("modelTypes", [data.value])}
                    dataCy="approval-model-type"
                    placeholder="Selecione o tipo de modelo"
                    size="sm"
                    disabled={isIssuer}
                  />
                )}

                {formState.errors.modelTypes?.message && (
                  <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
                    {formState.errors.modelTypes?.message}
                  </Text>
                )}
              </FormControl>
            </Col>

            {!isOnlyBrokenPolicyModel && (
              <Col sz="12">
                <FormControl name="approvalType" control={control} required>
                  <FieldLabel>Tipo de aprovação</FieldLabel>
                  <Select
                    dataCy="approval-model-approval-type"
                    options={approvalTypeOptions}
                    value={approvalTypeOptions.find((data) => data.value === approvalType)}
                    onChange={(data) => setValue("approvalType", data.value)}
                    placeholder="Selecione o tipo de aprovação"
                    size="sm"
                    disabled={isIssuer}
                  />
                  {formState.errors.approvalType?.message && (
                    <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
                      {formState.errors.approvalType?.message}
                    </Text>
                  )}
                </FormControl>
              </Col>
            )}

            <Col sz="12">
              <FormControl name="name" control={control} required>
                <FieldLabel>Nome</FieldLabel>
                <TextInput
                  dataCy="approval-model-name"
                  placeholder="Digite o nome do modelo de aprovação"
                  disabled={isIssuer}
                />
                {formState.errors.name && (
                  <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
                    {formState.errors.name.message}
                  </Text>
                )}
              </FormControl>
            </Col>

            {branches?.data?.length && (
              <Col sz="12">
                <FormControl name="branchName" control={control}>
                  <FieldLabel>Nome da filial</FieldLabel>
                  <Select
                    dataCy="approval-model-branch"
                    options={branches?.data}
                    isLoading={isLoadingBranches}
                    getOptionLabel={(item) => item.name}
                    getOptionValue={(item) => item.uuid}
                    placeholder="Selecione a filial"
                    size="sm"
                    disabled={isIssuer}
                  />
                  {formState.errors.branchName?.uuid && (
                    <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
                      {formState.errors.branchName.uuid.message}
                    </Text>
                  )}
                </FormControl>
              </Col>
            )}

            {isCostCenterType && !isOnlyBrokenPolicyModel && (
              <Col sz="12">
                <FormControl name="costCenters" control={control} required>
                  <FieldLabel>Nome do centro de custo</FieldLabel>
                  <MultiSelectWrap
                    dataCy="approval-model-cost-center"
                    size="sm"
                    hideSelectedOptions={false}
                    withMarkAll={true}
                    placeholder="Selecione o centro de custo"
                    getOptionLabel={(item: unknown) => (item as CostCenter).name}
                    getOptionValue={(item: unknown) => (item as CostCenter).uuid}
                    options={defaultCostCenters || []}
                    disabled={isIssuer}
                    setOptions={(selectedOptions) => {
                      setValue("costCenters", selectedOptions || []);
                      trigger("costCenters");
                    }}
                    onInputChange={fetchCostCenters}
                    onChange={async (costCenters: { uuid: string; name: string }[]) => {
                      if (costCenters.find(({ name }) => name === "Desmarcar todos")) {
                        setValue("costCenters", []);
                        return setCanMarkAllCostCenters(false);
                      }
                      if (costCenters.find(({ name }) => name === "Marcar todos")) {
                        setCanMarkAllCostCenters(true);
                        return;
                      }
                      setValue("costCenters", costCenters || []);
                      await trigger("costCenters");
                    }}
                  />
                  {formState.errors.costCenters && (
                    <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
                      {formState.errors.costCenters.message}
                    </Text>
                  )}
                </FormControl>
              </Col>
            )}

            {isGroupType && !isOnlyBrokenPolicyModel && (
              <Flex direction="column" gap="3">
                <Col sz="12">
                  <FormControl name="groups" control={control} required={isGroupType}>
                    <FieldLabel>Grupos</FieldLabel>
                    <MultiSelectWrap
                      dataCy="approval-model-group"
                      size="sm"
                      isLoading={isLoadingGroups}
                      placeholder="Selecione o grupo"
                      getOptionLabel={(item: unknown) => (item as CustomerEmployee).name}
                      getOptionValue={(item: unknown) => (item as CustomerEmployee).uuid}
                      disabled={isIssuer}
                      options={[
                        { uuid: "", name: "Desmarcar todos" },
                        { uuid: "", name: "Marcar todos" },
                        ...(availableGroups || []),
                      ]}
                      onInputChange={fetchGroups}
                      onChange={async (newGroups: { uuid: string; name: string }[]) => {
                        if (isPhaseType && newGroups.length > 1) {
                          setValue(
                            "groups",
                            newGroups.filter(
                              (newGroup) =>
                                !groups?.map((group) => group.uuid).includes(newGroup?.uuid),
                            ),
                          );
                          await trigger("groups");
                          return setCanMarkAllGroups(false);
                        }
                        if (newGroups.find(({ name }) => name === "Desmarcar todos")) {
                          setValue("groups", []);
                          return setCanMarkAllGroups(false);
                        }
                        if (newGroups.find(({ name }) => name === "Marcar todos")) {
                          setCanMarkAllGroups(true);
                          return setValue("groups", availableGroups || []);
                        }

                        setValue("groups", newGroups || []);
                        await trigger("groups");
                        return setCanMarkAllGroups(false);
                      }}
                    />
                    {formState.errors.groups && (
                      <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
                        {formState.errors.groups?.message}
                      </Text>
                    )}
                  </FormControl>
                </Col>
                <Col sz="12">
                  <FormControl name="groupApprovers" control={control} required>
                    <FieldLabel>Grupos de aprovação</FieldLabel>
                    <MultiSelectWrap
                      dataCy="approval-model-group-approver"
                      size="sm"
                      placeholder="Selecione os grupos de aprovação"
                      getOptionLabel={(item: unknown) => (item as GroupEmployee).name}
                      getOptionValue={(item: unknown) => (item as GroupEmployee).uuid}
                      isLoading={isLoadingApprovalGroups}
                      disabled={isIssuer}
                      options={[
                        { uuid: "", name: "Desmarcar todos" },
                        { uuid: "", name: "Marcar todos" },
                        ...(availableEmployeeGroups || []),
                      ]}
                      onInputChange={fetchApprovalGroups}
                      onChange={async (newGroupEmployees: { uuid: string; name: string }[]) => {
                        if (newGroupEmployees.find(({ name }) => name === "Marcar todos")) {
                          setCanMarkAllEmployeeGroups(true);
                          return setValue("groupApprovers", availableEmployeeGroups || []);
                        }
                        if (newGroupEmployees.length < (groupApprovers?.length || 0)) {
                          setRemoveApproverGroup(true);
                        }

                        setValue("groupApprovers", newGroupEmployees || []);
                        await trigger("groupApprovers");
                        setSelectApproverGroup(true);
                        return setCanMarkAllEmployeeGroups(false);
                      }}
                    />
                    {formState.errors.groupApprovers && (
                      <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
                        {formState.errors.groupApprovers.message}
                      </Text>
                    )}
                  </FormControl>
                </Col>
              </Flex>
            )}

            {(isPhaseType || isProjectType) && !isOnlyBrokenPolicyModel && (
              <Col sz="12">
                <FormControl name="projects" control={control} required>
                  <FieldLabel>{isPhaseType ? "Nome do projeto" : "Projetos"}</FieldLabel>
                  <MultiSelectWrap
                    dataCy="approval-model-project"
                    size="sm"
                    placeholder={`Selecione o${isPhaseType ? "" : "s"} projeto${
                      isPhaseType ? "" : "s"
                    }`}
                    getOptionLabel={(item: unknown) => (item as Project).name}
                    getOptionValue={(item: unknown) => (item as Project).uuid}
                    disabled={isIssuer}
                    options={[
                      ...(isProjectType
                        ? [
                            { uuid: "", name: "Desmarcar todos" },
                            { uuid: "", name: "Marcar todos" },
                          ]
                        : []),
                      ...(availableProjects || []),
                    ]}
                    onInputChange={fetchProjects}
                    onChange={async (newProjects: { uuid: string; name: string }[]) => {
                      if (isPhaseType && newProjects.length > 1) {
                        setValue(
                          "projects",
                          newProjects.filter(
                            (newProject) =>
                              !projects?.map((project) => project.uuid).includes(newProject?.uuid),
                          ),
                        );
                        await trigger("projects");
                        return setCanMarkAllProjects(false);
                      }
                      if (newProjects.find(({ name }) => name === "Desmarcar todos")) {
                        setValue("projects", []);
                        return setCanMarkAllProjects(false);
                      }
                      if (newProjects.find(({ name }) => name === "Marcar todos")) {
                        setCanMarkAllProjects(true);
                        return setValue("projects", availableProjects || []);
                      }

                      setValue("projects", newProjects || []);
                      await trigger("projects");
                      return setCanMarkAllProjects(false);
                    }}
                  />
                  {formState.errors.projects && (
                    <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
                      {formState.errors.projects.message}
                    </Text>
                  )}
                </FormControl>
              </Col>
            )}

            {isPhaseType && !isOnlyBrokenPolicyModel && (
              <Col sz="12">
                <FormControl name="phases" control={control} required>
                  <FieldLabel>Fases</FieldLabel>
                  <MultiSelectWrap
                    size="sm"
                    placeholder="Selecione as fases"
                    getOptionLabel={(item: unknown) => (item as Phase).name}
                    getOptionValue={(item: unknown) => (item as Phase).uuid}
                    disabled={isIssuer}
                    dataCy="approval-model-phase"
                    options={[
                      { uuid: "", name: "Desmarcar todos" },
                      { uuid: "", name: "Marcar todos" },
                      ...(availablePhases || []),
                    ]}
                    onInputChange={fetchPhases}
                    onChange={async (phases: { uuid: string; name: string }[]) => {
                      if (phases.find(({ name }) => name === "Desmarcar todos")) {
                        setValue("phases", []);
                        return setCanMarkAllPhases(false);
                      }
                      if (phases.find(({ name }) => name === "Marcar todos")) {
                        setCanMarkAllPhases(true);
                        return setValue("phases", availablePhases || []);
                      }

                      setValue("phases", phases || []);
                      await trigger("phases");
                      return setCanMarkAllPhases(false);
                    }}
                  />
                  {formState.errors.phases && (
                    <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
                      {formState.errors.phases.message}
                    </Text>
                  )}
                </FormControl>
              </Col>
            )}

            {!isOnlyBrokenPolicyModel && (
              <Col sz="12">
                <FormControl name="outPolicyApprovalModel" control={control}>
                  <FieldLabel>Modelo de aprovação para política violada</FieldLabel>
                  <AsyncSelect
                    dataCy="approval-model-out-policy-approval-model"
                    queryKey="fetch-approval-models"
                    defaultOptions={approvalModels ?? []}
                    fetchOptions={fetchApprovalModels}
                    isLoading={isLoadingApprovalModels}
                    getOptionLabel={(item) => item.name}
                    getOptionValue={(item) => item.uuid}
                    placeholder="Selecione o modelo de aprovação para a política violada"
                    size="sm"
                    disabled={isIssuer}
                  />
                  {formState.errors.outPolicyApprovalModel?.uuid && (
                    <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
                      {formState.errors.outPolicyApprovalModel.uuid.message}
                    </Text>
                  )}
                </FormControl>
              </Col>
            )}

            <Box>
              {formState.errors.levels && (
                <Text variant="error-base" size="2" css={{ fontWeight: "bold" }}>
                  {formState.errors.levels.message}
                </Text>
              )}

              {levels.map((level, index) => (
                <ApprovalLevelListItem
                  css={{
                    border: "1px solid $neutrals-light",
                    mt: "$2",
                    mb: "$4",
                    px: "$6",
                    py: "$6",
                    borderRadius: "$sm",
                  }}
                  key={`level-${index}`}
                  level={level as Level}
                  index={index}
                  control={control}
                  formState={formState}
                  fetchApprovers={fetchApproversWithOptions}
                  onSearchApprover={
                    isGroupType && !isOnlyBrokenPolicyModel
                      ? fetchApprovalGroupEmployees
                      : (name: string) => setSearchApprover(name)
                  }
                  defaultApprovers={defaultApprovers}
                  onSelectApprover={handleApproverSelection}
                  onAddLevel={onAddLevel}
                  onRemoveApprover={onRemoveApprover}
                  onRemoveLevel={onRemoveLevel}
                  isGroupType={isGroupType}
                  isBrokenPolicyModel={isOnlyBrokenPolicyModel}
                  isIssuer={isIssuer}
                />
              ))}
            </Box>
          </Row>

          {!isNew && (
            <>
              <Alert variant="warning" css={{ mb: "$4" }}>
                <Flex direction="column" gap="4">
                  <Flex align="center" gap="3">
                    <AlertIcon />
                    <Text variant="warning-dark" fw="700">
                      Alerta de alteração de modelo de aprovação
                    </Text>
                  </Flex>

                  <Text variant="warning-dark">
                    Ao alterar o modelo de aprovação, <strong>certifique-se</strong> que{" "}
                    <strong>não tenha nenhum pedido em aprovação para o {approvalTypeName}</strong>,
                    pois isso pode causar <strong>inconsistências </strong> no fluxo de aprovação.
                  </Text>
                </Flex>
              </Alert>

              <Flex align="center" gap="3" css={{ mb: "$5" }}>
                <Switch
                  checked={hasAcknowledgedWarning}
                  onChange={(event) => setHasAcknowledgedWarning(event.target.checked)}
                />
                <Text fw="700" variant="warning-dark">
                  Li e entendi o alerta acima
                </Text>
              </Flex>
            </>
          )}
        </Flex>

        <Flex
          justify="center"
          gap="4"
          css={{
            p: "$6",
            borderTop: "1px solid $neutrals-light",
            backgroundColor: "$neutrals-white",
            "@mxlg": {
              pb: "$1",
            },
          }}
        >
          {!isIssuer && (
            <Button variant="tertiary" type="button" onClick={onCloseClick}>
              <Text>Cancelar</Text>
            </Button>
          )}

          {!isIssuer && (
            <Button
              disabled={formState.isSubmitting || (!isNew && !hasAcknowledgedWarning)}
              type="submit"
              data-cy="approval-model-submit-button"
            >
              <Text>{isNew ? "Adicionar" : "Aplicar"}</Text>
            </Button>
          )}
        </Flex>
      </Form>
    </Flex>
  );
}
